import React, { memo, useEffect } from 'react';
import { useLocation } from 'wouter';
import { cn } from '../lib/utils';
import ScrollProgress from './ScrollProgress';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { DesktopNav, HeaderActions, HeaderBrand, MobileMenu } from './foundation/HeaderParts';

interface HeaderProps {
  isDark: boolean;
  scrolled: boolean;
  mobileMenuOpen: boolean;
  onToggleMobileMenu: () => void;
  onCloseMobileMenu: () => void;
  topBarVisible?: boolean;
}

const Header = memo(function Header({
  isDark,
  scrolled,
  mobileMenuOpen,
  onToggleMobileMenu,
  onCloseMobileMenu,
}: HeaderProps) {
  const [location] = useLocation();
  const mobileMenuRef = useFocusTrap<HTMLDivElement>(mobileMenuOpen);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        onCloseMobileMenu();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [mobileMenuOpen, onCloseMobileMenu]);

  return (
    <>
      <ScrollProgress color="hsl(var(--primary))" height={3} />

      <header
        className="fixed top-0 z-40 w-full border-b border-slate-200 bg-white transition-all duration-300 dark:border-slate-800 dark:bg-slate-950"
        role="banner"
      >
        <div className="mx-auto max-w-[1480px] px-4">
          <div
            className={cn(
              'mb-3 mt-3 flex items-center justify-between gap-3 rounded-[1.1rem] border border-slate-200 bg-white px-3 py-2.5 shadow-none transition-all duration-300 sm:gap-4 sm:px-4 dark:border-slate-700 dark:bg-slate-950',
              scrolled ? 'mb-2 mt-2' : 'mb-3 mt-3'
            )}
          >
            <HeaderBrand scrolled={scrolled} location={location} isDark={isDark} />

            <DesktopNav isDark={isDark} scrolled={scrolled} />

            <div className="flex items-center gap-2.5">
              <HeaderActions mobileMenuOpen={mobileMenuOpen} onToggleMobileMenu={onToggleMobileMenu} />
            </div>
          </div>
        </div>
        <MobileMenu mobileMenuOpen={mobileMenuOpen} isDark={isDark} mobileMenuRef={mobileMenuRef} onCloseMobileMenu={onCloseMobileMenu} />
      </header>
    </>
  );
});

Header.displayName = 'Header';

export default Header;
